// Message reaction gestures:
//   - double-tap / double-click  -> toggle a 🔥 reaction ("react_fire")
//   - long-press / right-click   -> open the emoji picker ("open_reaction_picker")
//
// Attached to each message bubble (`data-message-id`). Designed to coexist with the
// row's native horizontal scroll-snap: any meaningful movement cancels the long-press
// and disqualifies a double-tap, so a horizontal swipe never triggers a reaction.

import { pushRetryableEvent } from "./utils";

const DOUBLE_TAP_MS = 300;
const LONG_PRESS_MS = 450;
const MOVE_CANCEL_PX = 10;
const DOUBLE_TAP_DIST_PX = 40;
const RECENT_TOUCH_MS = 700;

const MessageReactions = {
    mounted() {
        this.messageId = this.el.dataset.messageId;

        this.lastTapTime = 0;
        this.lastTapX = 0;
        this.lastTapY = 0;
        this.lastTouchTime = 0;
        this.longPressTimer = null;
        this.longPressFired = false;
        this.startX = 0;
        this.startY = 0;

        this.onTouchStart = this.handleStart.bind(this);
        this.onTouchMove = this.handleMove.bind(this);
        this.onTouchEnd = this.handleEnd.bind(this);
        this.onContextMenu = this.handleContextMenu.bind(this);
        this.onDblClick = this.handleDblClick.bind(this);

        this.el.addEventListener("touchstart", this.onTouchStart, { passive: true });
        this.el.addEventListener("touchmove", this.onTouchMove, { passive: true });
        this.el.addEventListener("touchend", this.onTouchEnd);
        this.el.addEventListener("touchcancel", this.onTouchEnd);
        this.el.addEventListener("contextmenu", this.onContextMenu);
        this.el.addEventListener("dblclick", this.onDblClick);
    },

    destroyed() {
        this.cancelLongPress();
        this.el.removeEventListener("touchstart", this.onTouchStart);
        this.el.removeEventListener("touchmove", this.onTouchMove);
        this.el.removeEventListener("touchend", this.onTouchEnd);
        this.el.removeEventListener("touchcancel", this.onTouchEnd);
        this.el.removeEventListener("contextmenu", this.onContextMenu);
        this.el.removeEventListener("dblclick", this.onDblClick);
    },

    // A synthetic mouse event (dblclick/contextmenu) fired right after a touch should be
    // ignored — the touch handlers already covered it.
    recentTouch() {
        return Date.now() - this.lastTouchTime < RECENT_TOUCH_MS;
    },

    openPicker() {
        pushRetryableEvent(this, "open_reaction_picker", { message_id: this.messageId });
    },

    reactFire() {
        pushRetryableEvent(this, "react_fire", { message_id: this.messageId });
    },

    cancelLongPress() {
        if (this.longPressTimer) {
            clearTimeout(this.longPressTimer);
            this.longPressTimer = null;
        }
    },

    handleStart(event) {
        const point = event.touches ? event.touches[0] : event;
        this.startX = point.clientX;
        this.startY = point.clientY;
        this.lastTouchTime = Date.now();
        this.longPressFired = false;

        this.cancelLongPress();
        this.longPressTimer = setTimeout(() => {
            this.longPressFired = true;
            // reset double-tap tracking so the press isn't treated as a tap
            this.lastTapTime = 0;
            this.openPicker();
        }, LONG_PRESS_MS);
    },

    handleMove(event) {
        const point = event.touches ? event.touches[0] : event;
        const dx = Math.abs(point.clientX - this.startX);
        const dy = Math.abs(point.clientY - this.startY);
        if (dx > MOVE_CANCEL_PX || dy > MOVE_CANCEL_PX) {
            // it's a scroll/swipe, not a press or tap
            this.cancelLongPress();
            this.lastTapTime = 0;
        }
    },

    handleEnd(event) {
        this.cancelLongPress();
        this.lastTouchTime = Date.now();

        if (this.longPressFired) {
            this.longPressFired = false;
            event.preventDefault();
            return;
        }

        const point =
            event.changedTouches && event.changedTouches[0]
                ? event.changedTouches[0]
                : event;

        const now = Date.now();
        const withinTime = now - this.lastTapTime < DOUBLE_TAP_MS;
        const withinDist =
            Math.abs(point.clientX - this.lastTapX) < DOUBLE_TAP_DIST_PX &&
            Math.abs(point.clientY - this.lastTapY) < DOUBLE_TAP_DIST_PX;

        if (withinTime && withinDist) {
            // suppress the synthetic click/dblclick that would otherwise follow
            event.preventDefault();
            this.lastTapTime = 0;
            this.reactFire();
        } else {
            this.lastTapTime = now;
            this.lastTapX = point.clientX;
            this.lastTapY = point.clientY;
        }
    },

    handleContextMenu(event) {
        // always suppress the native menu; only open the picker for a real
        // desktop right-click (mobile long-press is handled by the timer above)
        event.preventDefault();
        if (!this.recentTouch()) {
            this.openPicker();
        }
    },

    handleDblClick(event) {
        // desktop double-click toggles fire; ignore synthetic dblclick from touch
        if (this.recentTouch()) return;
        event.preventDefault();
        this.reactFire();
    },
};

export default MessageReactions;
